import { EntregaLegislacaoDto } from "@/types/entregas/legislacao/entrega-legislacao-dto";
import { EntregaDocumentoDto } from "@/types/entregas/legislacao/entrega-documento-dto";
import { FiltrosLegislacao } from "@/types/entregas/legislacao/filtros-legislacao";
import { Legislacao } from "@/types/entregas/legislacao/legislacao";
import { Service } from "../mainService";
import { PortalError } from "@/types/portal-error";

const legislacaoService = {
  // filtros
  getTiposEntrega,
  consultarLegislacoes,
  getLegislacaoById,
  downloadDocumento,
  getExercicios,
  getLegislacaoEmAndamento,
  salvarLegislacao,
  getTiposLegislacao,
  getTiposAto,
  salvarDocumento,
  excluirDocumento,
  excluirLegislacao,
  enviarLegislacao,
};

/**
 * @returns tipos entrega.
 * @since 20221014
 */
async function getTiposEntrega(): Promise<any> {
  const response = await Service.$http.get(
    process.env.VUE_APP_ENTREGAS_API + "/legislacao/tiposEntrega"
  );
  if (response && (response.status === 200 || response.status === 204)) {
    return response.data;
  } else {
    throw new PortalError(
      response.data && response.data.msg
        ? response.data.msg
        : "Ocorreu um erro ao buscar os tipos de entrega"
    );
  }
}

/**
 * @returns tipos legislação.
 * @since 20221018
 */
async function getTiposLegislacao(): Promise<any> {
  const response = await Service.$http.get(
    process.env.VUE_APP_ENTREGAS_API + "/legislacao/tiposLegislacao"
  );
  if (response && (response.status === 200 || response.status === 204)) {
    return response.data;
  } else {
    throw new PortalError(
      response.data && response.data.msg
        ? response.data.msg
        : "Ocorreu um erro ao buscar os tipos de legislação"
    );
  }
}

/**
 * @returns tipos ato.
 * @since 20221018
 */
async function getTiposAto(tipoLegislacao): Promise<any> {
  const response = await Service.$http.get(
    process.env.VUE_APP_ENTREGAS_API + "/legislacao/tiposAto/" + tipoLegislacao
  );
  if (response && (response.status === 200 || response.status === 204)) {
    return response.data;
  } else {
    throw new PortalError(
      response.data && response.data.msg
        ? response.data.msg
        : "Ocorreu um erro ao buscar os tipos de ato"
    );
  }
}

/**
 * @returns exercicios.
 * @since 20221017
 */
async function getExercicios(): Promise<any> {
  const response = await Service.$http.get(
    process.env.VUE_APP_ENTREGAS_API + "/legislacao/exercicios"
  );
  if (response && (response.status === 200 || response.status === 204)) {
    return response.data;
  } else {
    throw new PortalError(
      response.data && response.data.msg
        ? response.data.msg
        : "Ocorreu um erro ao buscar os exercicios"
    );
  }
}

/**
 * @returns lista de Legislações
 * @since 20221014
 */
async function consultarLegislacoes(
  filtros: FiltrosLegislacao,
  page: number
): Promise<any> {
  const parametros = `?page=${page}&pageSize=10${filtros.toQueryString()}`;
  const response = await Service.$http.get(
    process.env.VUE_APP_ENTREGAS_API +
      "/legislacao/consultarLegislacoes" +
      parametros
  );

  if (response && (response.status === 200 || response.status === 204)) {
    return response.data;
  } else {
    throw new PortalError(
      response.data && response.data.msg
        ? response.data.msg
        : "Ocorreu um erro ao realizar a consulta. Verifique se os filtros de pesquisa aplicados podem ser refinados!"
    );
  }
}

/**
 * @returns Entrega de legislação
 * @since 20221014
 */
async function getLegislacaoById(id: number): Promise<any> {
  const response = await Service.$http.get(
    process.env.VUE_APP_ENTREGAS_API + "/legislacao/consultarLegislacao/" + id
  );
  if (response && response.status === 200) {
    return response.data;
  } else {
    throw new PortalError(
      response.data && response.data.msg
        ? response.data.msg
        : "Ocorreu um erro ao realizar a consulta."
    );
  }
}

/**
 * @returns Entrega em andamento
 * @since 20221017
 */
async function getLegislacaoEmAndamento(): Promise<any> {
  const response = await Service.$http.get(
    process.env.VUE_APP_ENTREGAS_API + "/legislacao/legislacaoEmAndamento"
  );
  if (response && (response.status === 200 || response.status === 204)) {
    return response.data;
  } else {
    throw new PortalError(
      response.data && response.data.msg
        ? response.data.msg
        : "Ocorreu um erro ao buscar entrega em andamento."
    );
  }
}

/**
 * @returns Salvar legislação
 * @since 20221018
 */
async function salvarLegislacao(
  legislacao: EntregaLegislacaoDto
): Promise<any> {
  const response = await Service.$http.post(
    process.env.VUE_APP_ENTREGAS_API + "/legislacao/salvarLegislacao",
    legislacao.toFormData()
  );
  if (response && (response.status === 200 || response.status === 204)) {
    return response.data;
  } else {
    throw new PortalError(
      response.data && response.data.msg
        ? response.data.msg
        : "Ocorreu um erro ao salvar a legislação."
    );
  }
}

/**
 * @returns Enviar legislação
 * @since 20221018
 */
async function enviarLegislacao(): Promise<any> {
  try {
    const response = await Service.$http.post(
      process.env.VUE_APP_ENTREGAS_API + "/legislacao/enviarLegislacao"
    );
    if (response && (response.status === 200 || response.status === 204)) {
      return response.data;
    } else {
      throw new PortalError(
        response.data && response.data.msg
          ? response.data.msg
          : "Ocorreu um erro ao enviar a legislação."
      );
    }
  } catch (e: any) {
    console.error("Falha ao enviar Legislação", e);
    throw new PortalError(e.response.data.msg ?? e);
  }
}

/**
 * @returns Salvar documento
 * @since 20221020
 */
async function salvarDocumento(documento: EntregaDocumentoDto): Promise<any> {
  let response;
  try {
    console.log("salvarDocumento", documento);
    response = await Service.$http.post(
      process.env.VUE_APP_ENTREGAS_API + "/legislacao/salvarDocumento",
      documento.toFormData()
    );
    return response.data;
  } catch (e: any) {
    throw new PortalError(
      e.response?.data?.msg ?? "Ocorreu um erro ao adicionar o documento."
    );
  }
}

/**
 * @returns Excluir documento
 * @since 20221020
 */
async function excluirDocumento(id): Promise<any> {
  const response = await Service.$http.delete(
    process.env.VUE_APP_ENTREGAS_API + "/legislacao/excluirDocumento/" + id
  );
  if (response && (response.status === 200 || response.status === 204)) {
    return response.data;
  } else {
    throw new PortalError(
      response.data && response.data.msg
        ? response.data.msg
        : "Ocorreu um erro ao excluir o documento."
    );
  }
}

/**
 * @returns Excluir legislacao
 * @since 20221020
 */
async function excluirLegislacao(): Promise<any> {
  const response = await Service.$http.delete(
    process.env.VUE_APP_ENTREGAS_API + "/legislacao/excluirLegislacao"
  );
  if (response && (response.status === 200 || response.status === 204)) {
    return response.data;
  } else {
    throw new PortalError(
      response.data && response.data.msg
        ? response.data.msg
        : "Ocorreu um erro ao excluir a entrega."
    );
  }
}

/**
 * @returns download documento.
 * @since 20221017
 */
async function downloadDocumento(idDocumento) {
  const response = await Service.$http.get(
    process.env.VUE_APP_ENTREGAS_API +
      "/legislacao/downloadDocumento/" +
      idDocumento,
    { responseType: "blob" }
  );

  if (response && response.status === 200) {
    return response.data;
  } else {
    throw new PortalError(
      response.data && response.data.msg
        ? response.data.msg
        : "Ocorreu um erro ao realizar o download."
    );
  }
}
export default legislacaoService;
